// Client-side mirror of core-api App\Services\Restaurant\Deliveroo\DeliverooUrl (task ordit1/tasks/tasks#169).
// Pure functions without imports, so they can be exercised outside the bundle as well.

const DELIVEROO_HOSTS = ['deliveroo.co.uk', 'www.deliveroo.co.uk']

// The server's /[\s\p{Z}\p{Cc}]/u: under /u PHP enables PCRE UCP, so \s is the Unicode whitespace class, but every
// character of it is in \p{Z} or \p{Cc} (HT, LF, VT, FF, CR are Cc), so this set is the same. JS \s is NOT used
// on purpose: it also matches U+FEFF (Cf), which the server accepts.
const FORBIDDEN_CHARACTERS = /[\p{Z}\p{Cc}]/u

// Raw scheme, authority and path, taken before new URL() normalises them (it drops a default port, resolves
// dot segments and lower-cases the host).
const RAW_URL_PARTS = /^([a-zA-Z][a-zA-Z0-9+.-]*):\/\/([^/?#]*)([^?#]*)/

/**
 * True for an http(s) URL on deliveroo.co.uk / www.deliveroo.co.uk (case-insensitive) whose path is
 * "/menu/<something>", with no user info, no port and no whitespace, Unicode separator or control character.
 */
export function isDeliverooMenuUrl(value) {
    if (typeof value !== 'string' || value === '') {
        return false
    }

    if (FORBIDDEN_CHARACTERS.test(value)) {
        return false
    }

    try {
        new URL(value)
    } catch (e) {
        return false
    }

    const parts = RAW_URL_PARTS.exec(value)
    if (!parts) {
        return false
    }

    const [, scheme, authority, path] = parts
    if (!['http', 'https'].includes(scheme.toLowerCase())) {
        return false
    }

    // User info ("user@", "user:pass@") and any port (":443" included) are rejected, like the server does.
    if (authority.includes('@') || authority.includes(':')) {
        return false
    }

    if (!DELIVEROO_HOSTS.includes(authority.toLowerCase())) {
        return false
    }

    return /^\/menu\/.+/i.test(path)
}

function toCoordinate(value) {
    if (typeof value === 'number') {
        return value
    }

    if (typeof value === 'string' && value.trim() !== '') {
        return Number(value.trim())
    }

    return NaN
}

/**
 * True when both pickup coordinates are set, finite and in range (latitude -90..90, longitude -180..180; 0 is
 * valid) — the server's DeliverooUrl::pickupCoordinates().
 */
export function hasUsablePickupCoordinates(latitude, longitude) {
    const lat = toCoordinate(latitude)
    const lon = toCoordinate(longitude)

    return (
        Number.isFinite(lat) &&
        Number.isFinite(lon) &&
        lat >= -90 &&
        lat <= 90 &&
        lon >= -180 &&
        lon <= 180
    )
}

// Exactly PHP trim()'s default set (space, \t, \n, \r, \0, \x0B): the server trims the value with it before the
// check. Not String.prototype.trim(), which also strips NBSP / Unicode spaces that the server rejects.
const PHP_TRIM_CHARACTERS = [' ', '\t', '\n', '\r', '\0', '\x0B']

function phpTrim(value) {
    if (typeof value !== 'string') {
        return value
    }

    let start = 0
    let end = value.length
    while (start < end && PHP_TRIM_CHARACTERS.includes(value[start])) {
        start++
    }
    while (end > start && PHP_TRIM_CHARACTERS.includes(value[end - 1])) {
        end--
    }

    return value.slice(start, end)
}

function isEmpty(value) {
    return value === null || value === undefined || value === ''
}

/** vee-validate check of the "deliveroo-url" rule: an empty (after PHP trim) value is allowed. */
export function validateDeliverooUrl(value) {
    const trimmed = phpTrim(value)

    return isEmpty(trimmed) || isDeliverooMenuUrl(trimmed)
}

/** vee-validate check of the "deliveroo-pickup" rule: a non-empty value needs usable pickup coordinates. */
export function validateDeliverooPickup(value, [latitude, longitude] = []) {
    return (
        isEmpty(phpTrim(value)) ||
        hasUsablePickupCoordinates(latitude, longitude)
    )
}
